import { useState, useEffect } from "react";
import { DONATE_URL } from "@/lib/links";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Send, Loader2, Mail } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Join() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadyIn, setAlreadyIn] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      setName((n) => n || user.name || "");
      setEmail((e) => e || user.email || "");
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Please enter your email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { name: name.trim(), email: email.trim() });
      setAlreadyIn(!!data.already);
      setDone(true);
      toast.success(data.already ? "You're already on the list, patriot!" : "Almost there — check your inbox to confirm.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <PageHero
        kicker="Stand With Us"
        title={<>Join The <span className="text-[#D90429]">Movement</span></>}
        subtitle="Add your name to the ranks. Sign up for the Patriot Power USA newsletter and get mission updates, rallies, trainings, and chapter news delivered straight to your inbox."
      />
      <section className="pt-16 pb-24 bg-muted min-h-[60vh]">
        <div className="max-w-xl mx-auto px-5 sm:px-8">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              data-testid="newsletter-success"
              className="bg-white border border-slate-200 p-10 text-center"
            >
              <CheckCircle2 className="w-16 h-16 text-[#D90429] mx-auto mb-6" />
              <h2 className="font-display text-3xl uppercase">{alreadyIn ? "Already Enlisted" : "Check Your Inbox"}</h2>
              <p className="text-slate-600 mt-3 max-w-md mx-auto">
                {alreadyIn
                  ? "You're already on the list, patriot. Watch your inbox for mission updates, events, and ways to get involved."
                  : "We just sent you a confirmation email. Click the link inside to confirm your address and lock in your spot on the list."}
              </p>
              <button
                data-testid="go-donate-button"
                onClick={() => window.open(DONATE_URL, "_blank", "noopener,noreferrer")}
                className="mt-8 inline-flex items-center gap-2 bg-[#D90429] text-white px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-95"
              >
                Support The Mission <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="newsletter-join-form" className="bg-white border border-slate-200 p-6 sm:p-10">
              <div className="flex items-center gap-3 mb-6">
                <Mail className="w-8 h-8 text-[#D90429]" />
                <h2 className="font-display text-2xl sm:text-3xl uppercase leading-tight">Sign Up For Updates</h2>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Full Name</label>
                  <input
                    data-testid="field-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="(FIRST) (LAST)"
                    className="w-full border border-slate-300 bg-white px-4 py-3 rounded-none focus:outline-none focus:border-[#D90429] focus:ring-1 focus:ring-[#D90429] transition-colors duration-200"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Email <span className="text-[#D90429]">*</span></label>
                  <input
                    data-testid="field-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full border border-slate-300 bg-white px-4 py-3 rounded-none focus:outline-none focus:border-[#D90429] focus:ring-1 focus:ring-[#D90429] transition-colors duration-200"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-subscribe-button"
                className="w-full mt-8 bg-[#D90429] text-white py-4 uppercase tracking-[0.2em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing Up…</> : <><Send className="w-4 h-4" /> Join The Newsletter</>}
              </button>
              <p className="text-xs text-slate-400 text-center mt-4">No spam, ever. Unsubscribe anytime.</p>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
